
import React, { useEffect, useState } from "react";
import {
  ShieldCheck,
  Headphones,
  MapPin,
  BadgeCheck,
  Tag,
  Building2,
} from "lucide-react";

const DEFAULT_TITLE = "Find your perfect stay in the heart of Dubai";

const BENEFITS = [
  {
    icon: Tag,
    title: "Best Hotel Deals",
    desc: "Affordable stays across Dubai",
  },
  {
    icon: ShieldCheck,
    title: "Secure Booking",
    desc: "Book with confidence",
  },
  {
    icon: Building2,
    title: "Great Hotel Choices",
    desc: "Hotels in popular locations",
  },
  {
    icon: Headphones,
    title: "Dedicated Support",
    desc: "Help when you need it",
  },
];

export default function Hero({
  title = DEFAULT_TITLE,
  subtitle = "Discover comfortable, affordable hotels in Dubai. Compare great stays and make every trip memorable.",
  backgroundImage,
  backgroundImages = [],
  variant = "inner",
  overlayOpacity = "",
  children,
  slideInterval = 5000,
}) {
  const isHome = variant === "home";

  const images = backgroundImages.length
    ? backgroundImages
    : backgroundImage
      ? [backgroundImage]
      : [];

  const [current, setCurrent] = useState(0);
  const [previous, setPrevious] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [isVisible, setIsVisible] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);

  const activeIndex = images.length
    ? current % images.length
    : 0;

  const activeImage = images[activeIndex];

  // Detect reduced-motion preference.
  useEffect(() => {
    const media = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    const update = () => setReduceMotion(media.matches);

    update();
    media.addEventListener?.("change", update);

    return () => {
      media.removeEventListener?.("change", update);
    };
  }, []);

  // Detect whether the browser tab is visible.
  useEffect(() => {
    const updateVisibility = () => {
      setIsVisible(!document.hidden);
    };

    updateVisibility();

    document.addEventListener(
      "visibilitychange",
      updateVisibility
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        updateVisibility
      );
    };
  }, []);

  // Preload only the first image immediately.
  useEffect(() => {
    if (!images.length) return;

    const firstImage = images[0];

    setLoadedImages((prev) => ({
      ...prev,
      [firstImage]: true,
    }));
  }, [images[0]]);

  // Preload the next slide.
  useEffect(() => {
    if (!isHome || images.length < 2) return;

    const nextIndex = (activeIndex + 1) % images.length;
    const nextImage = images[nextIndex];

    if (!nextImage || loadedImages[nextImage]) return;

    const image = new Image();

    image.src = nextImage;

    image.onload = () => {
      setLoadedImages((prev) => ({
        ...prev,
        [nextImage]: true,
      }));
    };

    return () => {
      image.onload = null;
    };
  }, [activeIndex, isHome, images, loadedImages]);

  // Automatically advance the slider.
  useEffect(() => {
    if (
      !isHome ||
      !isVisible ||
      reduceMotion ||
      images.length < 2 ||
      slideInterval <= 0
    ) {
      return;
    }

    const timer = window.setInterval(() => {
      setCurrent((index) => {
        const nextIndex = (index + 1) % images.length;

        setPrevious(index);

        return nextIndex;
      });
    }, slideInterval);

    return () => window.clearInterval(timer);
  }, [
    isHome,
    isVisible,
    reduceMotion,
    images.length,
    slideInterval,
  ]);

  // Clear previous slide after fade transition.
  useEffect(() => {
    if (previous === null) return;

    const timer = window.setTimeout(() => {
      setPrevious(null);
    }, reduceMotion ? 0 : 1000);

    return () => window.clearTimeout(timer);
  }, [previous, reduceMotion]);

  const changeSlide = (index) => {
    if (index === activeIndex) return;

    setPrevious(activeIndex);
    setCurrent(index);
  };

  // Render only active, previous, and preloaded next image.
  const background = (
    <div aria-hidden="true" className="absolute inset-0">
      {images.map((image, index) => {
        const shouldRender =
          index === activeIndex ||
          index === previous ||
          (loadedImages[image] && index ===
            (activeIndex + 1) % images.length);

        if (!shouldRender) return null;

        return (
          <div
            key={`${image}-${index}`}
            className={`
              absolute inset-0
              bg-cover bg-center
              ${reduceMotion
                ? ""
                : "transition-opacity duration-1000"
              }
              ${index === activeIndex
                ? "opacity-100"
                : "opacity-0"
              }
            `}
            style={{
              backgroundImage: `url("${image}")`,
            }}
          />
        );
      })}
    </div>
  );

  if (isHome) {
    return (
      <section className="relative mt-[114px] bg-white lg:mt-[120px]">

        {/* HERO BACKGROUND */}
        <div className="relative min-h-[590px] overflow-hidden bg-[#E9F6F0] sm:min-h-[640px] lg:min-h-[680px]">

          {background}

          {/* EMERALD / TEAL OVERLAY */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-b
              from-[#D6F2E4]/80
              via-[#A7D9C5]/45
              to-[#064E3B]/35
            "
          />

          <div
            className="
              absolute inset-0
              bg-gradient-to-r
              from-[#B4E4D0]/35
              via-transparent
              to-[#0D7664]/20
            "
          />

          {overlayOpacity && (
            <div className={`absolute inset-0 ${overlayOpacity}`} />
          )}

          {/* HERO CONTENT */}
          <div
            className="
              relative z-10 mx-auto
              flex min-h-[590px] max-w-[1200px]
              flex-col items-center justify-start
              px-5 pb-36 pt-16 text-center
              sm:min-h-[640px] sm:pt-20
              lg:min-h-[680px] lg:pt-24
            "
          >
            <div
              className="
                mb-5 inline-flex items-center gap-2
                rounded-full border border-[#B9DEC9]
                bg-white/90 px-5 py-2
                text-xs font-bold text-[#168B43]
                shadow-sm
              "
            >
              <MapPin size={15} aria-hidden="true" />
              DISCOVER DUBAI FOR LESS
            </div>

            <h1
              className="
                max-w-[900px]
                text-[39px] font-extrabold
                leading-[1.12] tracking-[-0.045em]
                text-[#064E3B]
                sm:text-[56px] lg:text-[70px]
              "
            >
              {title === DEFAULT_TITLE ? (
                <>
                  Find Your Perfect Stay
                  <span className="block text-[#168B43]">
                    in Dubai
                  </span>
                </>
              ) : (
                title
              )}
            </h1>

            {subtitle && (
              <p
                className="
                  mt-6 max-w-[650px]
                  text-sm leading-7 text-[#345A4D]
                  sm:text-base sm:leading-8
                "
              >
                {subtitle}
              </p>
            )}

            {/* TRUST LABELS */}
            <div
              className="
                mt-7 flex flex-wrap items-center
                justify-center gap-x-7 gap-y-3
                text-xs font-semibold text-[#064E3B]
              "
            >
              <span className="flex items-center gap-2">
                <BadgeCheck
                  size={17}
                  aria-hidden="true"
                  className="text-[#168B43]"
                />
                Great Hotel Deals
              </span>

              <span className="flex items-center gap-2">
                <ShieldCheck
                  size={17}
                  aria-hidden="true"
                  className="text-[#168B43]"
                />
                Secure Booking
              </span>

              <span className="flex items-center gap-2">
                <Headphones
                  size={17}
                  aria-hidden="true"
                  className="text-[#168B43]"
                />
                Local Support
              </span>
            </div>
          </div>

          {/* SLIDER NAVIGATION */}
          {images.length > 1 && (
            <div
              className="
                absolute bottom-24 left-1/2
                z-20 flex -translate-x-1/2 gap-2
              "
            >
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => changeSlide(index)}
                  aria-label={`Show slide ${index + 1}`}
                  aria-current={
                    index === activeIndex
                      ? "true"
                      : undefined
                  }
                  className={`
                    h-2.5 rounded-full transition-all
                    ${index === activeIndex
                      ? "w-9 bg-[#168B43]"
                      : "w-2.5 bg-[#168B43]/35 hover:bg-[#168B43]"
                    }
                  `}
                />
              ))}
            </div>
          )}
        </div>

        {/* FLOATING SEARCH FORM */}
        {children && (
          <div
            className="
              relative z-30 mx-auto
              -mt-[78px] w-full max-w-[1536px]
              px-4 sm:px-6 lg:px-8
            "
          >
            <div
              className="
                rounded-[20px]
                border border-[#E2EDE6]
                bg-white p-3
                shadow-[0_20px_60px_rgba(0,65,35,0.15)]
                sm:p-5
              "
            >
              {children}
            </div>
          </div>
        )}

        {/* BENEFITS ROW */}
        <div
          className="
            mx-auto grid max-w-[1240px]
            gap-6 px-5 pb-12 pt-12
            sm:grid-cols-2 sm:px-8
            lg:grid-cols-4
          "
        >
          {BENEFITS.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="flex items-center gap-3"
            >
              <div
                className="
                  flex h-12 w-12 shrink-0
                  items-center justify-center
                  rounded-full bg-[#E8F7EE]
                  text-[#168B43]
                "
              >
                <Icon
                  size={22}
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
              </div>

              <div>
                <h3 className="text-sm font-bold text-[#064E3B]">
                  {title}
                </h3>

                <p className="mt-1 text-xs text-[#64748B]">
                  {desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // INNER PAGE HERO
  return (
    <section
      className="
        relative mt-[114px]
        overflow-hidden bg-[#064E3B]
        lg:mt-[120px]
      "
    >
      {background}

      <div
        className="
          absolute inset-0
          bg-gradient-to-r
          from-[#032F22]/90
          via-[#064E3B]/70
          to-[#064E3B]/25
        "
      />

      {overlayOpacity && (
        <div className={`absolute inset-0 ${overlayOpacity}`} />
      )}

      <div
        className="
          relative z-10 mx-auto flex
          min-h-[290px] max-w-[1240px]
          items-center px-5 py-16
          sm:min-h-[330px] sm:px-8
        "
      >
        <div className="max-w-[850px]">
          <p
            className="
              mb-4 flex items-center gap-2
              text-xs font-bold uppercase
              tracking-[0.18em] text-[#A7F3C3]
            "
          >
            <MapPin size={15} aria-hidden="true" />
            DubaiBudgetiveHotels
          </p>

          <h1
            className="
              text-4xl font-extrabold
              leading-tight tracking-tight
              text-white sm:text-5xl lg:text-6xl
            "
          >
            {title}
          </h1>

          {subtitle && (
            <p
              className="
                mt-5 max-w-[660px]
                text-sm leading-7
                text-white/90 sm:text-base
              "
            >
              {subtitle}
            </p>
          )}

          {children && (
            <div className="mt-6">
              {children}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
